// ============================================================
// fondos/efectos/constellation.js — «Estrellas»: tres capas de parallax que parpadean y alguna
// estrella fugaz (efecto de LIENZO)
//
// Portado de `vendor/bg/constellation-bg.js` en la F2b del P3 con el MISMO dibujo (el test de
// espejo lo compara llamada a llamada con la clase de siempre). La velocidad va por `dt` y el reloj
// es el `t` del motor (antes, `Date.now()`).
//
// F3: le llegan por fin `speed` y `color2` (E14: los leía, pero el catálogo no se los declaraba). El
// segundo color es el de la marca por la paleta (`fondos/paleta.js`, como los fluidos): un 30 % de
// las estrellas. Sin velocidad en el dato va a 0,6× como siempre (= 3 en el panel). Y E13: el alfa
// sale del tope del catálogo (`alfaMax`) y sobre CLARO las estrellas crecen (`comun.js::TAM_CLARO`;
// las más pequeñas medían 0,3 px). En oscuro, el dibujo de siempre.
// ============================================================

import { pasos, rgbDe, interpolar, tamano } from './comun.js';

function capa(azar, n, minR, maxR, speed) {
    const estrellas = [];
    for (let i = 0; i < n; i++) {
        estrellas.push({
            x: azar(),
            y: azar(),
            // Movimiento muy lento y uniforme (parallax, no rebote).
            vx: (azar() - 0.5) * speed * 0.3,
            vy: (azar() - 0.5) * speed * 0.3,
            radius: minR + azar() * (maxR - minR),
            phase: azar() * Math.PI * 2,
            twinkleSpeed: 0.5 + azar() * 2.0,
            useColor2: azar() > 0.7,
        });
    }
    return estrellas;
}

function lanzarFugaz(s) {
    const azar = s.azar;
    const angle = 0.3 + azar() * 0.5;   // ~30-60 grados, entrando por arriba a la izquierda
    s.fugaces.push({
        x: azar() * 0.7,
        y: azar() * 0.3,
        vx: Math.cos(angle) * 0.015,
        vy: Math.sin(angle) * 0.015,
        life: 0,
        maxLife: 0.6 + azar() * 0.4,
        length: 0.05 + azar() * 0.1,
    });
}

export default {
    crear(ctx, e) {
        const p = e.params;
        // Densidad 1-10 → 40-200 estrellas (30-120 en táctil).
        const n = Math.round(interpolar(p.density, 5, e.tactil ? 30 : 40, e.tactil ? 120 : 200));
        const speedMult = p.speed != null ? (0.2 + (p.speed - 1) * (1.8 / 9)) : 0.6;
        const capas = [
            capa(e.azar, Math.round(n * 0.5), 0.3, 0.5, speedMult * 0.2),    // fondo: pequeñas y lentas
            capa(e.azar, Math.round(n * 0.35), 0.8, 1.2, speedMult * 0.5),   // medio
            capa(e.azar, Math.round(n * 0.15), 1.5, 2.5, speedMult * 1.0),   // frente: grandes y rápidas
        ];
        return {
            w: e.w, h: e.h, azar: e.azar,
            rgb: rgbDe(e.c1), rgb2: p.color2 ? rgbDe(p.color2) : null,
            alfa: e.alfa,
            tam: tamano(e.oscuro),
            capas: capas,
            fugaces: [],
            proximaFugaz: 3000 + e.azar() * 4000,   // ms hasta la primera estrella fugaz
            ultimaFugaz: 0,                          // el `t` de la última (el efecto nace en t 0)
        };
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t, dt) {
        const k = pasos(dt);
        const rgb = s.rgb;
        const rgb2 = s.rgb2;
        const W = s.w;
        const H = s.h;
        const time = t * 0.001;

        s.capas.forEach(function (estrellas) {
            estrellas.forEach(function (star) {
                if (k > 0) {
                    star.x += star.vx * k / W;
                    star.y += star.vy * k / H;
                    // Envuelven el borde: no rebotan.
                    if (star.x < 0) star.x = 1;
                    if (star.x > 1) star.x = 0;
                    if (star.y < 0) star.y = 1;
                    if (star.y > 1) star.y = 0;
                }
                const px = star.x * W;
                const py = star.y * H;
                const twinkle = 0.4 + 0.6 * (Math.sin(time * star.twinkleSpeed + star.phase) * 0.5 + 0.5);
                const alpha = s.alfa * twinkle;
                const c = (rgb2 && star.useColor2) ? rgb2 : rgb;
                // Halo suave: un círculo exterior translúcido y el núcleo.
                const r = star.radius * s.tam;
                if (star.radius > 1.2) {
                    ctx.beginPath();
                    ctx.arc(px, py, r * 2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha * 0.15})`;
                    ctx.fill();
                }
                ctx.beginPath();
                ctx.arc(px, py, r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
                ctx.fill();
            });
        });

        if (t - s.ultimaFugaz > s.proximaFugaz) {
            lanzarFugaz(s);
            s.ultimaFugaz = t;
            s.proximaFugaz = 3000 + s.azar() * 6000;
        }

        s.fugaces = s.fugaces.filter(function (ss) {
            ss.life += 0.04 * k;
            ss.x += ss.vx * k;
            ss.y += ss.vy * k;
            if (ss.life > ss.maxLife || ss.x > 1.1 || ss.y > 1.1) return false;

            // Aparece y se apaga.
            const lifeRatio = ss.life / ss.maxLife;
            const fadeAlpha = lifeRatio < 0.3 ? lifeRatio / 0.3 : 1 - ((lifeRatio - 0.3) / 0.7);
            const alpha = s.alfa * fadeAlpha;

            // Una línea con estela, del color principal.
            const ex = ss.x * W;
            const ey = ss.y * H;
            const tailLen = ss.length * W;
            const angle = Math.atan2(ss.vy, ss.vx);
            const tx = ex - Math.cos(angle) * tailLen;
            const ty = ey - Math.sin(angle) * tailLen;
            const grad = ctx.createLinearGradient(tx, ty, ex, ey);
            grad.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
            grad.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.5 * s.tam;
            ctx.beginPath();
            ctx.moveTo(tx, ty);
            ctx.lineTo(ex, ey);
            ctx.stroke();
            return true;
        });
    },
};
